// src/pages/WaterMeterRegistration.js
import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const WaterMeterRegistration = () => {
  const [formData, setFormData] = useState({
    serialNumber: '',
    bleAddress: '',
    gpsCoordinates: '',
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // AbortController refs
  const checkFieldAbortControllerRef = useRef();
  const uniqueCheckAbortControllerRef = useRef();
  const submitAbortControllerRef = useRef();

  // Local validation regexes
  const serialRegex = /^[A-Za-z0-9]{10}$/;
  const bleRegex = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;
  const gpsRegex = /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      const { [name]: removed, ...rest } = prev;
      return rest;
    });
  };

  const checkField = async (fieldName) => {
    const value = formData[fieldName];
    if (!value) return;

    // cancel any in-flight check
    checkFieldAbortControllerRef.current?.abort();
    const controller = new AbortController();
    checkFieldAbortControllerRef.current = controller;

    // Client-side format validation
    if (fieldName === 'serialNumber' && !serialRegex.test(value)) {
      setErrors(prev => ({ ...prev, serialNumber: 'Serial Number must be exactly 10 alphanumeric characters' }));
      return;
    }
    if (fieldName === 'bleAddress' && !bleRegex.test(value)) {
      setErrors(prev => ({ ...prev, bleAddress: 'BLE Address must be in format XX:XX:XX:XX:XX:XX' }));
      return;
    }

    // Backend existence check
    try {
      const res = await api.get(`/api/proxy/fieldcheck/${value}`, {
        params: { fieldName },
        signal: controller.signal
      });
      if (res.data.exists) {
        const message = fieldName === 'serialNumber'
          ? 'Serial Number already in use'
          : 'BLE Address already in use';
        setErrors(prev => ({ ...prev, [fieldName]: message }));
      }
    } catch {
      if (controller.signal.aborted) return;
      const message = fieldName === 'serialNumber'
        ? 'Error checking Serial Number'
        : 'Error checking BLE Address';
      setErrors(prev => ({ ...prev, [fieldName]: message }));
    }
  };

  const checkGpsFormat = () => {
    const value = formData.gpsCoordinates;
    if (!value) return;
    if (!gpsRegex.test(value)) {
      setErrors(prev => ({ ...prev, gpsCoordinates: 'Invalid GPS format. Use longitude,latitude' }));
    }
  };

  // Synchronous full-form validation (format only)
  const validateAll = () => {
    const newErrors = {};
    if (!serialRegex.test(formData.serialNumber)) {
      newErrors.serialNumber = 'Serial Number must be exactly 10 alphanumeric characters';
    }
    if (!bleRegex.test(formData.bleAddress)) {
      newErrors.bleAddress = 'BLE Address must be in format XX:XX:XX:XX:XX:XX';
    }
    if (formData.gpsCoordinates && !gpsRegex.test(formData.gpsCoordinates)) {
      newErrors.gpsCoordinates = 'Invalid GPS format. Use longitude,latitude';
    }
    return newErrors;
  };

  // Async full-form uniqueness check
  const checkAllUnique = async () => {
    // cancel any in-flight uniqueness check
    uniqueCheckAbortControllerRef.current?.abort();
    const controller = new AbortController();
    uniqueCheckAbortControllerRef.current = controller;

    const newErrors = {};
    const fields = ['serialNumber', 'bleAddress'];
    for (const fieldName of fields) {
      const value = formData[fieldName];
      if (!value || newErrors[fieldName]) continue;
      try {
        const res = await api.get(`/api/proxy/fieldcheck/${value}`, {
          params: { fieldName },
          signal: controller.signal
        });
        if (res.data.exists) {
          newErrors[fieldName] = fieldName === 'serialNumber'
            ? 'Serial Number already in use'
            : 'BLE Address already in use';
        }
      } catch {
        if (controller.signal.aborted) return newErrors;
        newErrors[fieldName] = fieldName === 'serialNumber'
          ? 'Error checking Serial Number'
          : 'Error checking BLE Address';
      }
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1) Format validation
    const formatErrors = validateAll();
    if (Object.keys(formatErrors).length > 0) {
      setErrors(formatErrors);
      return;
    }

    // 2) Uniqueness validation
    const uniqErrors = await checkAllUnique();
    if (Object.keys(uniqErrors).length > 0) {
      setErrors(uniqErrors);
      return;
    }

    const payload = {
      serialNumber: formData.serialNumber,
      bleAddress: formData.bleAddress,
    };

    // Only include and validate GPS if provided
    if (formData.gpsCoordinates) {
      const parts = formData.gpsCoordinates.split(',').map(p => p.trim());
      if (parts.length !== 2 || parts.some(p => isNaN(parseFloat(p)))) {
        setErrors(prev => ({ ...prev, gpsCoordinates: 'Invalid GPS format. Use longitude,latitude' }));
        return;
      }
      payload.gpsCoordinates = parts.map(p => parseFloat(p));
    }

    // cancel any in-flight submit
    submitAbortControllerRef.current?.abort();
    const controller = new AbortController();
    submitAbortControllerRef.current = controller;

    try {
      const res = await api.post('/api/proxy/wmeters/', payload, {
        signal: controller.signal
      });
      navigate(`/water-meters/${res.data._id.$oid || res.data._id}`);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err.response?.status === 403) {
        setErrors({ form: 'Error registering water meter: Action denied' });
      } else {
        setErrors({ form: 'Error registering water meter' });
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      checkFieldAbortControllerRef.current?.abort();
      uniqueCheckAbortControllerRef.current?.abort();
      submitAbortControllerRef.current?.abort();
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Paper sx={{ p: 4, width: 400 }}>
        <Typography variant="h5" align="center">
          Register Water Meter
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Serial Number"
            name="serialNumber"
            fullWidth
            margin="normal"
            value={formData.serialNumber}
            onChange={handleChange}
            onBlur={() => checkField('serialNumber')}
            error={!!errors.serialNumber}
            helperText={errors.serialNumber}
          />
          <TextField
            label="BLE Address"
            name="bleAddress"
            fullWidth
            margin="normal"
            value={formData.bleAddress}
            onChange={handleChange}
            onBlur={() => checkField('bleAddress')}
            error={!!errors.bleAddress}
            helperText={errors.bleAddress}
          />
          <TextField
            label="GPS Coordinates (optional)"
            name="gpsCoordinates"
            fullWidth
            margin="normal"
            value={formData.gpsCoordinates}
            onChange={handleChange}
            onBlur={checkGpsFormat}
            error={!!errors.gpsCoordinates}
            helperText={errors.gpsCoordinates || 'Format: longitude,latitude'}
          />
          {errors.form && <Typography color="error">{errors.form}</Typography>}
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
            Register
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default WaterMeterRegistration;
